<template>
  <aside v-if="isVisible" class="app-install-banner">
    <div class="app-install-banner__inner">
      <div class="app-install-banner__text">
        <div class="app-install-banner__title-row">
          <p class="app-install-banner__title">Приложение Vetsy</p>
          <span class="app-install-banner__badge">новинка</span>
        </div>
        <p class="app-install-banner__sub">{{ subtitle }}</p>
      </div>
      <a
          class="app-install-banner__cta"
          :href="APP_DOWNLOAD_PATH"
          v-analytics:app_install_banner="{content: place}"
      >Установить</a>
    </div>
    <button
        type="button"
        class="app-install-banner__close"
        aria-label="Закрыть баннер"
        @click="dismiss"
    >
      <SvgoClose class="app-install-banner__close-icon" aria-hidden="true"/>
    </button>
  </aside>
</template>

<script setup lang="ts">
import {APP_DOWNLOAD_PATH} from '~/utils/appDownload';

const props = defineProps<{
  /** Подпись под заголовком: у блога и ЛК она разная (макет `Баннеры для приложения`). */
  subtitle: string;
  /** Ключ места показа: закрытие в блоге не гасит баннер в личном кабинете. */
  place: string;
}>();

const storageKey = computed(() => `appInstallBannerDismissed:${props.place}`)

// SSR не знает, закрывал ли владелец баннер: рендерим только после гидрации,
// иначе на сервере уедет разметка, которую клиент тут же снимет.
const isVisible = ref(false)

onMounted(() => {
  try {
    isVisible.value = !localStorage.getItem(storageKey.value)
  } catch {
    isVisible.value = true
  }
})

const dismiss = (): void => {
  isVisible.value = false

  try {
    localStorage.setItem(storageKey.value, new Date().toISOString())
  } catch {
    // Приватный режим: баннер закроется до перезагрузки страницы.
  }
}
</script>

<style scoped lang="scss">
.app-install-banner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 8px 56px;
  background: $color-purple;
  color: $color-on-accent;

  @include media(sm) {
    padding: 8px 16px;
  }

  &__inner {
    width: 100%;
    max-width: 1280px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 160px;

    @include media(sm) {
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 12px;
    }
  }

  &__text {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.2);
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    white-space: nowrap;
  }

  &__sub {
    margin: 0;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.3;
    opacity: 0.6;
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 8px 20px;
    border-radius: 50px;
    background: $color-pink;
    color: $color-accent-text;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;

    @include media(sm) {
      align-self: flex-start;
    }
  }

  &__close {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 0;
    background: transparent;
    color: $color-on-accent;
    cursor: pointer;
    opacity: 1;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }

    @include media(sm) {
      right: 8px;
      top: 8px;
      transform: none;
      z-index: 2;
    }
  }

  &__close-icon {
    width: 14.4px;
    height: 14.4px;
    color: inherit;
  }
}
</style>
